import { useState, useEffect } from 'react';
import usePlacesService from 'react-google-autocomplete/lib/usePlacesAutocompleteService';
import { Autocomplete } from '@newulife/common';
import { TextField } from '@mui/material';
import env from 'config';
import { v2 } from 'components/common/form/formValidationRules';
import { InputErrorMessage } from 'components/common/inputs/Input';

const AddressAutocomplete = ({ config, ...props }) => {
  const [placeDetails, setPlaceDetails] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const googleAutoComplete = config.enrollment.form.sections.shipping.googleAutoComplete;
  const primaryField = config.enrollment.form.sections.shipping.fields[0];
  const primaryFieldKey = `${props.formType.toUpperCase()}_${primaryField}`;
  const { isValid: setValidCheckFromProps = () => {}, validationChecks, required = true } = props;
  const [validityString, set_validityString] = useState('untouched');
  const [errorMessageId, set_errorMessageId] = useState();

  const checkValueValidity = (value = '') => {
    const setError = (error = 'requiredFields') => {
      set_validityString('error');
      set_errorMessageId(error);
    };
    const setValid = () => {
      set_validityString('valid');
      set_errorMessageId();
    };

    if (!value && required) {
      setError();
    } else if (!value && !required) {
      setValid();
    } else if (value) {
      v2(validationChecks)(value).then((errorMessage) => {
        if (!errorMessage) {
          setValid();
        } else {
          setError(errorMessage);
        }
      });
    }
  };

  // Check for validity on component mount
  useEffect(() => {
    if (props.value) {
      checkValueValidity(props.value);
    }
  }, []);

  // Notify parent of validity changes
  useEffect(() => {
    setValidCheckFromProps(validityString === 'valid');
  }, [validityString]);

  const { placesService, placePredictions, getPlacePredictions, isPlacePredictionsLoading } =
    usePlacesService({
      apiKey: env.GOOGLE_PLACES_API_KEY,
      debounce: 300,
      ...googleAutoComplete,
    });

  const parseAddressComponents = (addressComponents) => {
    const parsed = {};
    addressComponents.forEach((component) => {
      const types = component.types;
      const prefix = `${props.formType.toUpperCase()}_`;
      if (types.includes('postal_code')) parsed[`${prefix}ZIP`] = component.long_name;
      if (types.includes('locality')) parsed[`${prefix}CITY`] = component.long_name;
      if (types.includes('administrative_area_level_1'))
        parsed[`${prefix}STATE`] = component.long_name;
      let streetNumber = '';
      let route = '';
      let sublocality = '';
      let neighborhood = '';
      if (types.includes('street_number')) streetNumber = component.long_name;
      if (types.includes('route')) route = component.long_name;
      if (types.includes('sublocality')) sublocality = component.long_name;
      if (types.includes('neighborhood')) neighborhood = component.long_name;
      if (streetNumber || route || sublocality || neighborhood)
        parsed[`${prefix}ADDRESS1`] =
          `${streetNumber} ${route} ${sublocality} ${neighborhood}`.trim();
      if (types.includes('administrative_area_level_2'))
        parsed[`${prefix}COUNTY`] = component.long_name;
    });
    return parsed;
  };

  const handleSelection = (newValue) => {
    const selectedPrediction = placePredictions.find(
      (prediction) => prediction.description === newValue,
    );
    if (selectedPrediction && placesService) {
      placesService.getDetails({ placeId: selectedPrediction.place_id }, (data) => {
        if (data && data.address_components) {
          const parsedAddress = parseAddressComponents(data.address_components);
          const primaryField = config.enrollment.form.sections.shipping.fields[0];
          const fieldKey = `${props.formType.toUpperCase()}_${primaryField}`;
          const value = parsedAddress[fieldKey] || '';

          setInputValue(value);
          props.onFieldsChange(parsedAddress);
          checkValueValidity(value);
        }
      });
    }
  };

  return (
    <Autocomplete
      value={props.value || null}
      freeSolo
      disableClearable
      options={placePredictions.map((item) => item.description)}
      renderOption={(optionProps, option) => (
        <li {...optionProps} key={option}>
          {option}
        </li>
      )}
      inputValue={inputValue}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
        getPlacePredictions({ input: newInputValue });

        props.onFieldsChange({ [primaryFieldKey]: newInputValue });
        // props.isValid(validateFn(newInputValue)); // Use your validation function
      }}
      onChange={(event, newValue) => {
        if (newValue) handleSelection(newValue);
      }}
      renderInput={(params) => (
        <>
          <TextField
            {...params}
            label={props.label}
            onChange={(evt) => {
              const value = evt.target.value;
              getPlacePredictions({ input: value });

              if (validityString === 'error') {
                set_validityString('untouched');
                set_errorMessageId();
              }
            }}
            onBlur={(evt) => {
              checkValueValidity(evt.target.value);
            }}
            error={validityString === 'error'}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 0,
                fontSize: 16,
                paddingY: '2px',
                '& fieldset': {
                  borderWidth: validityString === 'error' ? '2px' : '1px',
                },
              },
              '& .MuiInputLabel-root.Mui-focused, & .MuiInputLabel-root.Mui-error': {
                fontWeight: 700,
              },
              '& .MuiFormHelperText-root': {
                fontWeight: 700,
                color: '#9a3b26',
              },
            }}
          />
          {errorMessageId && (
            <InputErrorMessage
              dictionary={props.dictionary}
              id={props.id}
              messageId={errorMessageId}
              styleOverride={{ marginBottom: 0 }}
            />
          )}
        </>
      )}
    />
  );
};

export default AddressAutocomplete;
